import { useState, type ReactNode } from 'react'
import type { TmsLoadInvoiceCheck, TmsLoadRow } from '@numeo/types-main-web-app'
import { Badge, Button } from '@/components/ds2'
import { InvoiceApproval } from './InvoiceApproval'
import { LoadFixActions, useFixSlots } from './LoadFixActions'
import { bolSlotOf } from './bolSlot'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import { documentSlotsOf, LoadRow } from './LoadRow'
import { invoiceCellOf, laneOf, LOAD_STAGE_LABEL } from './format'
import { useFreshRows } from './freshRows'
import { loadKeyOf } from './loadKey'
import { AMAZON_STATE, freshInvoicedOf, invoiceLabelOf, tonuStateOf } from './loadRowStates'
import { documentActions, fixesOf, invoiceLoadsMessage, type RowAction } from './rowActions'
import { InvoicedLabel } from './InvoicedLabel'
import { useCanCreateInvoices } from './useCanCreateInvoices'
import { useInvoicedAs } from './threadInvoices'
import { useUpdatedBelow } from './updatedBelow'
import { AssignDriverAction } from '../assign/AssignDriverAction'
import { AssignDriverPanel } from '../assign/AssignDriverPanel'
import { deliveryPassedWordsOf, pickupPassedWordsOf, type OverdueDelivery } from '../assign/assignModel'
import { useAssignStore, type AssignTarget } from '../assign/assignStore'
import { useStoredAssign } from '../assign/StoredAssignsContext'
import { useCanAssignLoads } from '../assign/useCanAssignLoads'
import { deliveryPassedOf, pickupAtOf, pickupPassedOf } from './loadRowStates'

interface LoadsTableProps {
    rows: TmsLoadRow[]
    /** The company's IANA zone the list answer names: the overdue dates read in it. */
    timezone?: string
    onSend: (message: string) => void
}

/** The truck a listed row shows as its card drew it: its truck and drivers, when it has one. */
const snapshotTargetOf = (row: TmsLoadRow): AssignTarget | undefined =>
    row.truck_id || row.truck_unit
        ? {
              truckId: row.truck_id ?? '',
              truckLabel: row.truck_unit ?? '',
              drivers: (row.drivers ?? []).map((driver) => driver.name).filter((name): name is string => Boolean(name)),
          }
        : undefined

/** A truck's name for its badge: "Truck 214", whether the answer sent the number or the name. */
const truckWordsOf = (unit: string): string => (/^truck\b/i.test(unit) ? unit : `Truck ${unit}`)

/** The state word of a delivered load: its invoice, the one the chat made, else what its row was drawn with. */
const DeliveredState = ({ row, invoicedAs }: { row: TmsLoadRow; invoicedAs: string | undefined }) => {
    // A load with an invoice is invoiced: the same words as every other invoiced load in the block.
    const invoice = invoicedAs ?? row.invoice?.invoice_number ?? (row.invoice ? '' : undefined)
    if (invoice !== undefined) return <InvoicedLabel number={invoice || null} />
    return (
        <span className="inline-flex items-center gap-1">
            {LOAD_STAGE_LABEL[row.status] ?? row.status} · {invoiceCellOf(row)}
        </span>
    )
}

/**
 * One listed load: the shared load line. A delivered load shows its three document slots, and the Ready-tab check the list drew
 * (`invoice_check`) decides its action: an Invoice that opens its approval here, or the fixes it still needs under it. A TONU
 * cancellation bills its fee and says so; an Amazon-billed load has no action.
 */
const ListedLoad = ({ row, timezone, onSend }: { row: TmsLoadRow; timezone?: string; onSend: (message: string) => void }) => {
    const fresh = useFreshRows((state) => state.rows[row.load_id])
    const invoicedAs = useInvoicedAs(row.load_id, row.load_number) ?? (fresh ? freshInvoicedOf(fresh) : undefined)
    const dimmed = useUpdatedBelow(loadKeyOf(row))
    const canInvoice = useCanCreateInvoices()
    const [approving, setApproving] = useState(false)
    const [asked, setAsked] = useState(false)
    const [fixOpen, setFixOpen] = useState(false)
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const canAssign = useCanAssignLoads()
    const assigned = useAssignStore((s) => s.assigned[row.load_id])
    const stored = useStoredAssign(row.load_id, row.load_number)
    const pending = useAssignStore((s) => s.pending[row.load_id])
    const [picking, setPicking] = useState(false)
    // The Assign actions exist only for a user who may edit loads, on an upcoming load, in a chat that has a tab.
    const assignRow = canAssign && !!tabId && row.status === 'upcoming'
    const assignOpen = assignRow && (picking || !!pending)
    const delivered = row.status === 'delivered'
    const number = row.load_number
    // The row as the Ready tab reads it since the chat drew it (after its paperwork changed), else the check the list drew.
    const check: TmsLoadInvoiceCheck | undefined = fresh
        ? { invoiceable: !!fresh.invoiceable, reasons: fresh.reasons ?? [], billed_through_amazon: fresh.billed_through_amazon, red_flagged: fresh.red_flagged }
        : row.invoice_check
    const documents = fresh?.documents ?? row.document_chips
    const amazon = !!check?.billed_through_amazon
    // The rate con is required to invoice: a load without one is not offered an invoice here.
    const invoiceable = !!check?.invoiceable && !invoicedAs && !amazon && !!number && documents?.rate_con !== false
    // The fixes are the check's reasons; a row drawn before the check existed takes its fixes from its document chips, as before.
    // A rate-less load that can be invoiced bills a $0 line haul: its rate is a warning, not a fix offered here (as on the Ready list).
    // An invoiced load still takes its POD: a factor can ask for it after invoicing, so its POD upload or review stays offered.
    const invoiced = Boolean(invoicedAs || row.invoice)
    const fixes: RowAction[] = (() => {
        if (amazon || !number) return []
        if (invoiced) return documentActions(documents).filter((step) => step.kind === 'upload_pod' || step.kind === 'review_pod')
        const offered = check ? fixesOf(check.reasons, documents) : delivered ? documentActions(documents) : []
        return check?.invoiceable ? offered.filter((step) => step.kind !== 'set_rate') : offered
    })()

    // An upcoming load with a truck shows the truck and its drivers as a badge: the truck assigned here, until the chat's next read.
    const upcoming = row.status === 'upcoming'
    // A passed delivery date is the overdue line; the pickup line shows only when the delivery has not passed.
    const deliveryPassed = deliveryPassedOf(row)
    const overdue: OverdueDelivery | undefined = deliveryPassed ? { at: row.delivery_at, timezone } : undefined
    const pickupPassed = upcoming && !deliveryPassed && pickupPassedOf(row)
    // The truck the row shows: this session's assign, else the chat's stored write (null: taken off), else the card's own truck.
    const held: AssignTarget | undefined = assigned?.target ?? (stored ? (stored.target ?? undefined) : snapshotTargetOf(row))
    const truckBadge =
        upcoming && held?.truckLabel ? (
            <Badge variant="soft" type="default" size="sm" label={[truckWordsOf(held.truckLabel), ...held.drivers].join(' · ')} />
        ) : null
    // A passed delivery or pickup keeps the route line and adds its warning beside it.
    const overdueWords = deliveryPassed
        ? deliveryPassedWordsOf(row.delivery_at, timezone)
        : pickupPassed
          ? pickupPassedWordsOf(pickupAtOf(row), timezone)
          : null
    const stageLine: ReactNode = overdueWords ? (
        <span className="inline-flex min-w-0 items-center gap-2">
            <span className="truncate">{laneOf(row.origin, row.destination)}</span>
            <Badge variant="soft" type="warning" size="sm" label={overdueWords} />
        </span>
    ) : (
        `${LOAD_STAGE_LABEL[row.status] ?? row.status} · ${laneOf(row.origin, row.destination)}`
    )
    const state: ReactNode = row.tonu ? (
        tonuStateOf(row.broker_rate_usd)
    ) : amazon ? (
        AMAZON_STATE
    ) : delivered ? (
        <DeliveredState row={row} invoicedAs={invoicedAs} />
    ) : (
        <span className="inline-flex min-w-0 items-center gap-2">
            {stageLine}
            {truckBadge}
        </span>
    )

    const slots = useFixSlots({ actions: fixes, loadNumber: number ?? '', loadId: row.load_id, onOpen: () => setFixOpen(true) })
    const invoiceNode: ReactNode =
        invoiceable && canInvoice ? (
            asked ? (
                <span role="status" className="text-xs text-success">
                    Asked to invoice
                </span>
            ) : (
                <Button size="sm" disabled={approving} onClick={() => setApproving(true)}>
                    {invoiceLabelOf(row.tonu)}
                </Button>
            )
        ) : null
    // The row's action column: the invoice, or the row's first fix with the rest behind "⋯" (no line under the row).
    // A refused or failed attempt leaves the row's action back in place; an attempt in flight or a question hides it.
    const busy = pending?.phase === 'running' || pending?.phase === 'question'
    const openPicker = () => {
        useAssignStore.getState().setPending(row.load_id, undefined)
        setPicking(true)
    }
    const action: ReactNode = assignRow ? (
        <AssignDriverAction tabId={tabId ?? ''} loadId={row.load_id} hasTruck={!!held} target={held} picking={picking || busy} onOpen={openPicker} />
    ) : number && (fixes.length > 0 || invoiceNode) ? (
        <LoadFixActions
            loadNumber={number}
            loadId={row.load_id}
            actions={fixes}
            onSend={onSend}
            lead={invoiceNode ?? undefined}
            open={fixOpen}
            onOpenChange={setFixOpen}
        />
    ) : (
        (invoiceNode ?? undefined)
    )

    return (
        <li className="py-1.5 first:pt-0 last:pb-0">
            <LoadRow
                number={number ?? ''}
                title={row.broker ?? '—'}
                state={state}
                documents={delivered || row.tonu ? documentSlotsOf(documents) : undefined}
                slots={{ ...slots, bol: delivered && !amazon ? bolSlotOf({ loadNumber: number, loadId: row.load_id, onFiled: notify }) : undefined }}
                amount={row.broker_rate_usd}
                action={action}
                dimmed={dimmed}
                redFlag={check?.red_flagged}
                details={
                    assignOpen && tabId ? (
                        <AssignDriverPanel
                            tabId={tabId}
                            loadId={row.load_id}
                            picking={picking}
                            overdue={overdue}
                            onClose={() => setPicking(false)}
                            onPickAnother={openPicker}
                        />
                    ) : approving && number ? (
                        <InvoiceApproval
                            loadNumbers={[number]}
                            total={row.broker_rate_usd}
                            onCreate={() => {
                                onSend(invoiceLoadsMessage([number]))
                                setApproving(false)
                                setAsked(true)
                            }}
                            onCancel={() => setApproving(false)}
                        />
                    ) : undefined
                }
            />
        </li>
    )
}

/**
 * Several loads (`tms_loads.list`) as the shared load lines (design §3, §4). A delivered load shows its three document slots, its
 * invoice action (approved in its own line before anything is sent) or the fixes it needs, right here.
 */
export const LoadsTable = ({ rows, timezone, onSend }: LoadsTableProps) => (
    <ul className="flex flex-col">
        {rows.map((row) => (
            <ListedLoad key={row.load_id} row={row} timezone={timezone} onSend={onSend} />
        ))}
    </ul>
)
